import type { EventContentArg } from '@fullcalendar/core';
import { Movie, Timer, WatchLater } from '@mui/icons-material';
import { Box, Link, Typography } from '@mui/material';
import { alpha, darken, getContrastRatio, lighten } from '@mui/material/styles';
import { DateTime } from 'luxon';
import { Tooltip } from '~/components/Tooltip';
import { theme } from '~/components/theme';
import { formatDate } from '~/lib/datetime';
import { i18n } from '~/lib/i18n';
import type { NetworkScheduler__Event as Event } from '../queries/events.generated';

// FullCalendar calls this as a plain render function, not a component, so no hooks here.
const { palette } = theme;

export const CalendarEvent = (event: EventContentArg) => {
  const { event: rawEvent, isBuffer } = event.event.extendedProps as {
    event: Event;
    isBuffer: boolean;
    isDisabled: boolean;
  };

  // eslint-disable-next-line no-restricted-syntax -- fallback for a user-picked event color
  const color = rawEvent.eventType.color ?? '#ccc';
  const backgroundColor = isBuffer ? alpha(color, 0.75) : color;
  const borderColor = darken(color, 0.5);
  // Event-type colours are user-picked, so the text picks black or white per block to clear
  // 4.5:1 (WCAG 1.4.3). A buffer block is 75% over the white calendar, which lighten mirrors.
  const composited = isBuffer ? lighten(color, 0.25) : color;
  const textColor =
    getContrastRatio(palette.common.white, composited) >= 4.5
      ? palette.common.white
      : palette.common.black;
  const eventName = rawEvent.name.replace(/.*\b(vs. .*)/, '$1');

  const startTime = formatDate(isBuffer ? rawEvent.contentStart : rawEvent.start, {
    format: DateTime.TIME_SIMPLE,
    stripZeroMinutes: true,
  });

  const endTime = formatDate(isBuffer ? rawEvent.contentStop : rawEvent.stop, {
    format: DateTime.TIME_SIMPLE,
    stripZeroMinutes: true,
  });

  const getOverrideTooltip = () => {
    const count = rawEvent.overrideCount;
    return i18n.t('pages.events.event_card.override_tooltip_plural', { count });
  };

  const getBufferTooltip = () => {
    return i18n.t('pages.events.event_card.buffer_tooltip', {
      startTime,
      endTime,
    });
  };

  const card = (
    <Box
      sx={{
        backgroundColor,
        borderLeft: `4px solid ${borderColor}`,
        borderRadius: 1,
        color: textColor,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        px: 1,
        overflow: 'hidden',
        width: '100%',
      }}
    >
      {/* Row 1: Logo + Event Type */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, overflow: 'hidden' }}>
          {rawEvent.eventType.iconFile?.uri && (
            <Box
              alt=""
              component="img"
              src={rawEvent.eventType.iconFile.uri}
              sx={{ height: 32, width: 32, mr: 1 }}
            />
          )}
          <Typography
            variant="subtitle1"
            fontWeight={600}
            noWrap
            sx={{ color: 'inherit', overflow: 'hidden', textOverflow: 'ellipsis' }}
          >
            {rawEvent.eventType.name}
          </Typography>
        </Box>
        {!isBuffer && (rawEvent.sponsorStartTime || rawEvent.sponsorStopTime) && (
          <Tooltip
            describeChild={false}
            title={i18n.t('pages.events.event_card.sponsor_override_tooltip')}
          >
            <Timer
              fontSize="small"
              titleAccess={i18n.t('pages.events.event_card.sponsor_override_tooltip')}
            />
          </Tooltip>
        )}
        {!isBuffer && rawEvent.overrideCount > 0 && (
          <Tooltip describeChild={false} title={getOverrideTooltip()}>
            <Movie fontSize="small" titleAccess={getOverrideTooltip()} />
          </Tooltip>
        )}
        {isBuffer && (
          <Tooltip describeChild={false} title={getBufferTooltip()}>
            <WatchLater fontSize="small" titleAccess={getBufferTooltip()} />
          </Tooltip>
        )}
      </Box>

      {/* Row 2: Event Name */}
      <Typography
        variant="subtitle2"
        fontWeight={500}
        noWrap
        title={rawEvent.name}
        sx={{ color: 'inherit' }}
      >
        {eventName}
      </Typography>

      {/* Row 3: Time Range */}
      <Typography variant="body2" sx={{ mb: 0.5 }}>
        {startTime} - {endTime}
      </Typography>
    </Box>
  );

  // The other views render the event itself as a link to its url, but the list view's row is
  // not one, so there the card carries the url to give the keyboard a way to the event.
  if (event.view.type.startsWith('list') && event.event.url) {
    return (
      <Link color="inherit" href={event.event.url} sx={{ display: 'block' }} underline="none">
        {card}
      </Link>
    );
  }

  return card;
};
